/* eslint-disable eqeqeq */
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Heading,
  Box,
  Grid,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Checkbox,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import InputMask from "react-input-mask";

import { formatDateWithDay } from "../../utils/dateUtils";

const AddVolunteer = ({
  isOpen,
  onClose,
  type,
  isLoading,
  pickupSlot,
  updateItem,
  fetchPickupSlots,
  shouldLoadFormData,
  createVolunteerUser,
}) => {
  const [validationMessage, setValidationMessage] = useState("");

  const [formData, setFormData] = useState({
    // Initialize with empty or default values for each field
    firstName: "",
    lastName: "",
    mobilePhone: "",
    canAccomodate: "",
    isDriver: false,
    isGreeter: true,
    canDriveChurchVan: false,
  });

  useEffect(() => {
    if (shouldLoadFormData) {
      loadFormDataFromLocalStorage();
      // Any other logic that needs to run when the modal opens
    }
  }, [shouldLoadFormData]);

  const loadFormDataFromLocalStorage = () => {
    const savedFormData = localStorage.getItem("volunteerFormData");
    const expirationTimestamp = localStorage.getItem("volunteerFormExpiration");

    if (savedFormData && expirationTimestamp) {
      const currentTime = new Date().getTime();
      if (currentTime < parseInt(expirationTimestamp)) {
        const savedData = JSON.parse(savedFormData);
        // Update only specific fields from saved data
        setFormData((prevFormData) => ({
          ...prevFormData,
          firstName: savedData.firstName || "",
          lastName: savedData.lastName || "",
          mobilePhone: savedData.mobilePhone || "",
        }));
      } else {
        // Clear expired data
        localStorage.removeItem("volunteerFormData");
        localStorage.removeItem("volunteerFormExpiration");
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.firstName || !formData.lastName || !formData.mobilePhone) {
      setValidationMessage("Please fill in name and phone number");
      return;
    }

    if (type == "Driver" && !formData.canAccomodate) {
      setValidationMessage(
        "Please fill in How Many People You Can Accommodate"
      );
      return;
    }

    // Phone number format validation
    const phoneRegex = /^\(\d{3}\)\d{3}-\d{4}$/;
    if (!phoneRegex.test(formData.mobilePhone)) {
      setValidationMessage(
        "Invalid phone number format. Expected: (000)000-0000"
      );
      return;
    }

    // Save formData to localStorage
    localStorage.setItem("volunteerFormData", JSON.stringify(formData));

    // Set a timestamp for expiration (30 days)
    const thirtyDaysInMilliseconds = 30 * 24 * 60 * 60 * 1000;
    const expirationTimestamp = new Date().getTime() + thirtyDaysInMilliseconds;
    localStorage.setItem(
      "volunteerFormExpiration",
      expirationTimestamp.toString()
    );

    loadFormDataFromLocalStorage(); // Reload form data from local storage

    // Generate a unique id

    // Create the volunteer data
    const newVolunteer = {
      ...formData,
      isDriver: type === "Driver",
      isGreeter: type === "Greeter",
    };

    // Ensure pickupSlot.volunteers is an array
    const existingVolunteers = pickupSlot.volunteers || [];

    // Add the new volunteer to the pickup slot
    const updatedSlot = {
      ...pickupSlot,
      volunteers: [...existingVolunteers, newVolunteer],
    };

    await updateItem(updatedSlot._id, updatedSlot);

    setFormData({}); // Reset form after submission
    fetchPickupSlots();

    createVolunteerUser(newVolunteer);

    onClose(); // Close modal after submission
  };
  return (
    pickupSlot && (
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader> {type} Volunteer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <form id="createStudentForm" onSubmit={handleSubmit}>
              {/* Student Details */}
              <Box mb={6}>
                <Heading size="md" mb={4}>
                  {formatDateWithDay(pickupSlot.pickupDateTime)}{" "}
                </Heading>
                {validationMessage && (
                  <Box color="red.500" mb={4}>
                    {validationMessage}
                  </Box>
                )}
                <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                  {/* First Name */}
                  <FormControl isRequired mb={4}>
                    <FormLabel>First Name</FormLabel>
                    <Input
                      name="firstName"
                      onChange={handleChange}
                      value={formData?.firstName || ""}
                    />
                  </FormControl>

                  {/* Last Name */}
                  <FormControl isRequired mb={4}>
                    <FormLabel>Last Name</FormLabel>
                    <Input
                      name="lastName"
                      onChange={handleChange}
                      value={formData?.lastName || ""}
                    />
                  </FormControl>

                  {/* Mobile Phone */}
                  <FormControl isRequired mb={4}>
                    <FormLabel>Mobile Phone</FormLabel>
                    <InputMask
                      mask="(999)999-9999"
                      value={formData.mobilePhone}
                      onChange={handleChange}
                      disabled={isLoading}
                    >
                      {(inputProps) => (
                        <Input {...inputProps} name="mobilePhone" />
                      )}
                    </InputMask>
                  </FormControl>

                  {type == "Driver" && (
                    /* Capacity */
                    <FormControl isRequired mb={4}>
                      <FormLabel>Capacity</FormLabel>
                      <NumberInput>
                        <NumberInputField
                          name="canAccomodate"
                          onChange={handleChange}
                          value={formData?.canAccomodate || ""}
                        />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                  )}
                  {type == "Driver" && (
                    <FormControl mb={4}>
                      <Checkbox
                        name="canDriveChurchVan"
                        onChange={handleChange}
                        checked={formData.canDriveChurchVan}
                      >
                        I will drive the church's van
                      </Checkbox>
                    </FormControl>
                  )}
                </Grid>
              </Box>
            </form>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handleSubmit}>
              {isLoading ? <Spinner size="sm" /> : "Volunteer"}
            </Button>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )
  );
};

export default AddVolunteer;
